import { Bot } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTablero, useTableros } from '@/hooks/useTareas';
import {
  useActualizarAutomatizacion, useAutomatizaciones, type Automatizacion, type CambioAutomatizacion,
} from '@/hooks/useTareasAutomatizacion';

const campo = 'px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50';
const DIAS_MIN = 1;
const DIAS_MAX = 90;

/** Tareas que el sistema crea solo; cada clínica las activa, ajusta los días y elige tablero y columna. */
export function AutomatizacionesPanel() {
  const { data: automatizaciones = [], isLoading } = useAutomatizaciones();

  return (
    <div>
      <h4 className="text-sm font-semibold text-text-primary mb-1 flex items-center gap-1.5">
        <Bot size={15} className="text-cyan-600" /> Tareas automáticas
      </h4>
      <p className="text-xs text-text-secondary mb-3">
        El sistema crea estas tareas por ti. Una misma situación nunca genera dos tareas, aunque se repita.
      </p>
      {isLoading && <p className="text-sm text-text-secondary">Cargando…</p>}
      <ul className="space-y-3">
        {automatizaciones.map(a => <Fila key={a.evento} automatizacion={a} />)}
      </ul>
    </div>
  );
}

function Fila({ automatizacion: a }: { automatizacion: Automatizacion }) {
  const actualizar = useActualizarAutomatizacion();
  const { data: tablerosData } = useTableros();
  const { data: tablero } = useTablero(a.tableroId ?? undefined);
  const tableros = (tablerosData?.tableros ?? []).filter(t => !t.archivado);

  const guardar = (cambios: CambioAutomatizacion) => actualizar.mutate({ evento: a.evento, ...cambios });

  return (
    <li className={cn('rounded-xl border p-3', a.activo ? 'border-gray-200' : 'border-gray-100 bg-gray-50')}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary">{a.titulo}</p>
          <p className="text-xs text-text-secondary">{a.descripcion}</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0" title={a.activo ? 'Desactivar' : 'Activar'}>
          <input
            type="checkbox"
            className="sr-only peer"
            checked={a.activo}
            onChange={e => guardar({ activo: e.target.checked })}
            aria-label={`${a.titulo}: ${a.activo ? 'activa' : 'apagada'}`}
          />
          <span className="w-9 h-5 rounded-full bg-gray-300 peer-checked:bg-primary-500 transition-colors" />
          <span className="absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
        </label>
      </div>

      {a.activo && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-text-secondary">
          <label className="inline-flex items-center gap-2">
            Vence en
            <input
              key={a.diasVencimiento}
              type="number"
              min={DIAS_MIN}
              max={DIAS_MAX}
              defaultValue={a.diasVencimiento}
              onBlur={e => {
                const dias = Math.min(DIAS_MAX, Math.max(DIAS_MIN, Math.round(Number(e.target.value) || a.diasVencimiento)));
                if (dias !== a.diasVencimiento) guardar({ diasVencimiento: dias });
                else e.target.value = String(a.diasVencimiento);
              }}
              className={cn(campo, 'w-16 text-center')}
            />
            día{a.diasVencimiento === 1 ? '' : 's'}
          </label>

          <label className="inline-flex items-center gap-2">
            Tablero
            <select
              value={a.tableroId ?? ''}
              onChange={e => guardar({ tableroId: e.target.value || null, columnaId: null })}
              className={campo}
            >
              <option value="">Predeterminado</option>
              {tableros.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          </label>

          <label className="inline-flex items-center gap-2">
            Columna
            <select
              value={a.columnaId ?? ''}
              disabled={!a.tableroId}
              onChange={e => guardar({ columnaId: e.target.value || null })}
              className={campo}
            >
              <option value="">Primera columna</option>
              {(tablero?.columnas ?? []).map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </label>
        </div>
      )}
    </li>
  );
}
